import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import classNames from 'classnames';
import StudentList from '../StudentList/StudentList';
import SchoolList from '../SchoolList/SchoolList';
import { Student, useOverviewContext } from '@/context/OverviewContext';
import { isTeacherPreview } from '@/utils/toolSettingsUtils';

interface StudentSwitcherModalProps {
  fetchStudents: () => Promise<Student[]>;
  onClose: () => void;
  onStudentSelect: () => void;
  className?: string;
  /** Teachers always use the school picker (`SchoolList`), even if this is omitted. */
  variant?: 'student' | 'school';
}

const ModalHeader: React.FC<{ title: string }> = ({ title }) => (
  <h2 className="student-modal-title">{title}</h2>
);

const StudentSwitcherModal: React.FC<StudentSwitcherModalProps> = ({
  fetchStudents,
  onClose,
  onStudentSelect,
  className,
  variant = 'student',
}) => {
  const modalClass = classNames('student-modal', className);
  const { teacherSchools, students: overviewStudents, loading: overviewLoading, userRoleVal } =
    useOverviewContext();
  const teacherPreview = isTeacherPreview(userRoleVal);
  const resolvedVariant: 'student' | 'school' =
    variant === 'school' || teacherPreview ? 'school' : 'student';

  const [students, setStudents] = useState<Student[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (resolvedVariant !== 'student') return;

    const loadStudents = async () => {
      setLoading(true);
      try {
        const data = await fetchStudents();
        setStudents(data);
      } catch {
        setStudents([]);
      } finally {
        setLoading(false);
      }
    };

    void loadStudents();
  }, [fetchStudents, resolvedVariant]);

  useEffect(() => {
    if (resolvedVariant !== 'school') return;
    setLoading(overviewLoading);
  }, [resolvedVariant, overviewLoading]);

  const title = resolvedVariant === 'school' ? 'Select a school' : 'Select a student';

  return ReactDOM.createPortal(
    <div
      className="student-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div className={modalClass}>
        <ModalHeader title={title} />
        {loading ? (
          <div className="student-loading-spinner">
            <svg data-testid="spinner" className="spinner" viewBox="0 0 50 50">
              <circle className="spinner-bg" cx="25" cy="25" r="20" fill="none" strokeWidth="5" />
              <circle className="spinner-fg" cx="25" cy="25" r="20" fill="none" strokeWidth="5" />
            </svg>
          </div>
        ) : resolvedVariant === 'school' ? (
          <SchoolList
            schools={teacherSchools}
            previewStudents={overviewStudents}
            onSelect={() => onStudentSelect()}
          />
        ) : (
          <StudentList
            students={students}
            showTaskBadge={!teacherPreview}
            onSelect={() => {
              onStudentSelect();
            }}
          />
        )}
      </div>
    </div>,
    document.body
  );
};

export default StudentSwitcherModal;
